import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@/shared/lib/utils';

export interface RangeSliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
    neutralValue?: number;
    rangeClassName?: string;
}

const RangeSlider = React.forwardRef<
    React.ElementRef<typeof SliderPrimitive.Root>,
    RangeSliderProps
>(({ className, min = 0, max = 100, step = 1, value, onValueChange, neutralValue = 0, rangeClassName, ...props }, ref) => {
    const val = value?.[0] ?? min;
    const percentage = (v: number) => ((v - min) / (max - min)) * 100;

    const start = Math.min(neutralValue, val);
    const end = Math.max(neutralValue, val);

    const left = percentage(start);
    const width = percentage(end) - left;

    return (
        <SliderPrimitive.Root
            ref={ref}
            className={cn('relative flex w-full touch-none select-none items-center py-2 cursor-pointer', className)}
            min={min}
            max={max}
            step={step}
            value={value}
            onValueChange={onValueChange}
            {...props}
        >
            <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-white/10">
                <div
                    className={cn('absolute h-full bg-accent rounded-full', rangeClassName)}
                    style={{
                        left: `${left}%`,
                        width: `${width}%`
                    }}
                />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb className="block h-2 w-2 rounded-full border border-white/35 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.35)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-50" />
        </SliderPrimitive.Root>
    );
});

RangeSlider.displayName = 'RangeSlider';

export { RangeSlider };